/**
 * Jixxeo XO Battle — real-time 3x3 tic-tac-toe with optional coin wagers.
 * You are always "X" if you were paired first, otherwise "O".
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MpShell } from "./_mpShell";
import { useMpClient } from "./_mpClient";

const LINES = [
  [0,1,2],[3,4,5],[6,7,8],
  [0,3,6],[1,4,7],[2,5,8],
  [0,4,8],[2,4,6],
];

function winner(b) {
  for (const [a,b2,c] of LINES) if (b[a] && b[a] === b[b2] && b[a] === b[c]) return { who: b[a], line: [a,b2,c] };
  if (b.every(Boolean)) return { who: "draw" };
  return null;
}

export default function JixxeoXOBattle() {
  const loc = useLocation();
  const challenge = new URLSearchParams(loc.search).get("challenge");
  const [stake, setStake] = useState(0);
  const [started, setStarted] = useState(!!challenge);
  const [key, setKey] = useState(0);
  const [board, setBoard] = useState(Array(9).fill(0));
  const [myTurn, setMyTurn] = useState(false);
  const [mySym, setMySym] = useState(1);   // 1 for X, 2 for O

  const client = useMpClient({
    game: "xo-battle", stake, challenge,
    autoConnect: started,
  });

  // Reset on new match
  useEffect(() => {
    setBoard(Array(9).fill(0));
  }, [key]);

  // On pair, decide symbol
  useEffect(() => {
    if (client.status === "paired") {
      setMySym(client.youFirst ? 1 : 2);
      setMyTurn(client.youFirst);
    }
  }, [client.status, client.youFirst]);

  // Handle opponent moves (including bot echoes)
  useEffect(() => {
    if (!client.lastMove) return;
    const m = client.lastMove;
    // bot move payloads: { from_bot: true, move: { cell } }
    // opponent move: { from_sid, move: { cell } }
    const cell = m?.move?.cell;
    if (typeof cell !== "number" || cell < 0 || cell > 8) return;
    setBoard((b) => {
      if (b[cell]) return b;
      const nb = [...b];
      nb[cell] = mySym === 1 ? 2 : 1;
      return nb;
    });
    setMyTurn(true);
  }, [client.lastMove, mySym]);

  // Detect game outcome and report
  useEffect(() => {
    const w = winner(board);
    if (!w) return;
    if (w.who === "draw") client.sendResult("tie");
    else if (w.who === mySym) client.sendResult("win");
    else client.sendResult("lose");
    setMyTurn(false);
  }, [board]);  // eslint-disable-line

  const play = (i) => {
    if (!myTurn) return;
    if (board[i]) return;
    if (client.status !== "paired") return;
    const nb = [...board]; nb[i] = mySym;
    setBoard(nb);
    setMyTurn(false);
    client.sendMove({ cell: i, board: nb });
  };

  const w = useMemo(() => winner(board), [board]);

  return (
    <MpShell title="Jixxeo XO Battle" accent="#00F0FF" game="xo-battle" gameSlug="jixxeo-xo-battle"
      status={started ? client.status : "idle"}
      self={client.self} opponent={client.opponent}
      settlement={client.settlement} stake={stake} setStake={setStake}
      onFindOpponent={() => { setStarted(true); setTimeout(() => client.connect(), 0); }}
      onRematch={() => { client.close(); setKey((k) => k + 1); setStarted(false); setTimeout(() => { setStarted(true); client.connect(); }, 100); }}
      initialChallenge={challenge}>
      <div data-testid="xo-body">
        <div className="mono text-[10px] uppercase tracking-widest text-white/60 mb-3 text-center">
          You are {mySym === 1 ? "X" : "O"} · {myTurn ? "Your turn" : "Opponent's turn"}
        </div>
        <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
          {board.map((v, i) => {
            const isWin = w && w.line && w.line.includes(i);
            return (
              <button key={i} data-testid={`xo-cell-${i}`} onClick={() => play(i)}
                disabled={!myTurn || !!v}
                className="aspect-square rounded-xl border-2 flex items-center justify-center font-heading font-black text-4xl transition-all"
                style={{
                  background: v === 1 ? "rgba(0,240,255,0.15)" : v === 2 ? "rgba(255,0,60,0.15)" : "rgba(255,255,255,0.04)",
                  borderColor: v === 1 ? "#00F0FF" : v === 2 ? "#FF003C" : "rgba(255,255,255,0.12)",
                  boxShadow: isWin ? "0 0 20px currentColor" : "none",
                  color: v === 1 ? "#00F0FF" : v === 2 ? "#FF003C" : "#fff",
                  opacity: !myTurn && !v ? 0.7 : 1,
                }}>
                {v === 1 ? "X" : v === 2 ? "O" : ""}
              </button>
            );
          })}
        </div>
      </div>
    </MpShell>
  );
}
